/* eslint-disable max-statements */
import React from 'react';
import styles from './styles.module.scss';
import classNames from 'classnames';
import { getPositionX } from './utils';
import icArrowLeft from 'assets/svg/ic_back.svg';
import ImageWithPlaceholder from 'src/Components/ImageWithPlaceholder';
import Skeleton from 'src/Components/Skeleton';

const Carousel = ({
  banners,
  className
}) => {
  const [width, setWidth] = React.useState(document.body.clientWidth);
  React.useEffect(() => {
    const handleResize = () => {
      setWidth(document.body.clientWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  const length = banners.length;
  const data = [...banners, ...banners, ...banners];

  const sleep = (ms = 0) => {
    return new Promise(resolve => setTimeout(resolve, ms));
  };

  const createItem = (position, idx) => {
    const item = {
      styles: {
        transform: `translateX(${position * 100}%)`,
        opacity: 1
      },
      ...data[idx]
    };

    if (position < length - 1 || position > length + 1) {
      item.styles.opacity = 0;
    }

    return item;
  };

  const keys = Array.from(Array(data.length).keys());
  const [items, setItems] = React.useState(keys);
  const [isTicking, setIsTicking] = React.useState(false);
  const [activeIdx, setActiveIdx] = React.useState(0);
  const bigLength = items.length;
  const prevClick = React.useCallback((jump = 1 && length > 1) => {
    if (!isTicking) {
      setIsTicking(true);
      setItems(prev => {
        return prev.map((_, i) => prev[(i + jump) % bigLength]);
      });
    }
  }, [bigLength, isTicking]);
  const nextClick = React.useCallback((jump = 1) => {
    if (!isTicking && length > 1) {
      setIsTicking(true);
      setItems(prev => {
        return prev.map((_, i) => prev[(i - jump + bigLength) % bigLength]);
      });
    }
  }, [bigLength, isTicking]);

  const handleDotClick = idx => {
    if (idx < activeIdx) prevClick(activeIdx - idx);
    if (idx > activeIdx) nextClick(idx - activeIdx);
  };

  React.useEffect(() => {
    if (isTicking) sleep(300).then(() => setIsTicking(false));
  }, [isTicking]);
  React.useEffect(() => {
    setActiveIdx((length - items[0] % length) % length);
  }, [items, length]);
  React.useEffect(() => {
    if (length > 1) {
      const timer = setInterval(() => nextClick(), 5000);
      return () => clearInterval(timer);
    }
  }, [nextClick]);
  const dragging = React.useRef(false);
  const threshHold = 100;
  const currentTranslate = React.useRef(0);
  const prevTranslate = React.useRef(0);
  const startPos = React.useRef(0);

  const touchStart = e => {
    startPos.current = getPositionX(e);
    dragging.current = true;
  };

  const touchMove = e => {
    if (dragging.current) {
      const currentPosition = getPositionX(e);
      currentTranslate.current = prevTranslate.current + currentPosition - startPos.current;
    }
  };

  const touchEnd = () => {
    dragging.current = false;
    const movedBy = currentTranslate.current - prevTranslate.current;
    if (movedBy < -threshHold) nextClick();
    if (movedBy > threshHold) prevClick();
  };

  const mouseLeave = () => {
    if (dragging.current) touchEnd();
  };

  const renderCarouselSlideItem = ({
    pos,
    idx
  }) => {
    const item = createItem(pos, idx);
    return /*#__PURE__*/React.createElement("li", {
      className: styles.slideItem,
      style: { ...item.styles,
        width: width + 'px'
      }
    }, /*#__PURE__*/React.createElement("a", {
      href: item.link,
      target: "_blank",
      className: styles.imgLink,
      rel: "noreferrer"
    }, /*#__PURE__*/React.createElement(ImageWithPlaceholder, {
      src: item.image,
      draggable: "false",
      className: styles.img,
      renderPlaceholder: () => /*#__PURE__*/React.createElement(Skeleton, {
        className: styles.spinner
      })
    })));
  };

  return /*#__PURE__*/React.createElement("div", {
    className: classNames(styles.wrap, className)
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.inner
  }, length > 1 && /*#__PURE__*/React.createElement("button", {
    className: classNames(styles.btn, styles.prev),
    onClick: () => prevClick()
  }, /*#__PURE__*/React.createElement("img", {
    className: classNames(styles.icon, styles.left),
    src: icArrowLeft
  })), /*#__PURE__*/React.createElement("div", {
    className: styles.container
  }, /*#__PURE__*/React.createElement("ul", {
    className: styles.slideList,
    style: {
      width: (length + 0.5) * width * 2 + 'px'
    }
  }, items.map((pos, i) => /*#__PURE__*/React.createElement("div", {
    key: i,
    onTouchStart: touchStart,
    onMouseDown: touchStart,
    onTouchMove: touchMove,
    onMouseMove: touchMove,
    onTouchEnd: touchEnd,
    onMouseUp: touchEnd,
    onMouseLeave: mouseLeave,
    onContextMenu: e => {
      e.preventDefault();
      e.stopPropagation();
    }
  }, renderCarouselSlideItem({
    pos,
    idx: i
  }))))), length > 1 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("button", {
    className: classNames(styles.btn, styles.next),
    onClick: () => nextClick()
  }, /*#__PURE__*/React.createElement("img", {
    className: classNames(styles.icon, styles.right),
    src: icArrowLeft
  })), /*#__PURE__*/React.createElement("div", {
    className: styles.dots
  }, items.slice(0, length).map((_, i) => /*#__PURE__*/React.createElement("button", {
    key: i,
    onClick: () => handleDotClick(i),
    className: classNames(styles.dot, i === activeIdx ? styles.active : null)
  }))))));
};

export default Carousel;